/**
 * Parse suggested responses from LLM output that may contain <suggested_actions> XML tags
 * Returns cleaned content and extracted suggestions.
 *
 * IMPORTANT: This function must NOT alter character positions in the returned
 * content (e.g. via .trim()) because tool-call markers recorded during
 * streaming are byte-offset indices into the *original* accumulated text.
 * Trimming would shift those positions and cause post-tool characters to
 * render before the tool badge instead of after it.
 */
export function parseSuggestedResponses(
  content: string,
): { content: string; suggestions: string[] } {
  if (!content || typeof content !== 'string') {
    return { content: content || '', suggestions: [] };
  }

  // Aggregate <sa> across EVERY <suggested_actions> block, not just the first — a turn can carry
  // more than one block (a model-authored one + a system back-fill), and a chip must never be
  // hidden by block order. Dedupe by chip text and cap at 3.
  const blockPattern = /<suggested_actions>(.*?)<\/suggested_actions>/gs;
  const blocks = [...content.matchAll(blockPattern)];

  if (blocks.length === 0) {
    return { content, suggestions: [] };
  }

  // Parse individual <sa ...>...</sa> tags. `[^>]*` on the open tag so a chip carrying any
  // attribute still matches — a plain-text `<sa>` regex would silently drop it.
  const saPattern = /<sa\b[^>]*>(.*?)<\/sa>/gs;
  const suggestions: string[] = [];
  const seen = new Set<string>();
  for (const block of blocks) {
    for (const m of block[1].matchAll(saPattern)) {
      const text = m[1]?.trim();
      if (!text || seen.has(text) || suggestions.length >= 3) continue;
      seen.add(text);
      suggestions.push(text);
    }
  }

  // Remove ALL suggested_actions blocks from the content. They always sit at the very end of the
  // response, after all tool markers, so removing them does not shift any marker positions.
  const cleanedContent = content.replace(blockPattern, '');

  return { content: cleanedContent, suggestions };
}

/**
 * Single canonical "finalize an agent message" parser. Extracts the
 * `<conclusion>` body, any `<suggested_actions>` inside it, and the
 * pre-conclusion text (kept as `thinkingContent` for the collapsible
 * reasoning view).
 *
 * Used by BOTH the normal completion path (chatbotStore.ts) AND the
 * questionnaire-resume path. Sharing one function prevents the resume
 * path from drifting and forgetting to extract suggestions — which is
 * what caused post-questionnaire messages to render without action
 * buttons in production.
 *
 * `rawContent` is the full final response.content string. Callers that
 * receive a structured `content` object (questionnaire payload) should
 * bypass this helper.
 */
// Matches one <conclusion>...</conclusion> block. Non-greedy body (`*?`) so
// each block matches individually; the global flag lets matchAll find them all.
// Tolerant of attributes / casing / whitespace because non-Anthropic models
// (Gemini 3.5 Flash) emit looser markup than Anthropic.
const CONCLUSION_BLOCK_RE = /<conclusion\b[^>]*>([\s\S]*?)<\/conclusion\s*>/gi;
const CONCLUSION_OPEN_RE = /<conclusion\b[^>]*>/gi;

/**
 * Locate the LAST <conclusion> block — the authoritative reply.
 *
 * A turn can carry more than one conclusion: a non-Anthropic model
 * (Gemini 3.5 Flash) routinely emits a *draft* conclusion in an earlier phase
 * before the render step writes the real one. The render step's output always
 * comes last, so the last block is the reply. A greedy
 * `<conclusion>([\s\S]*)</conclusion>` would instead span the first opening tag
 * to the last closing tag, sweeping the internal `<triage>` / post-turn audit
 * between them into the visible reply — the #4205 / #4207 leak.
 *
 * Returns the body (inner text) of the last block and the index of its opening
 * tag, or null if there's no opening tag. Tolerates an unclosed final block
 * (truncated stream): from the last opening tag to end-of-string.
 */
function findLastConclusion(content: string): { body: string; openIdx: number } | null {
  const matches = [...content.matchAll(CONCLUSION_BLOCK_RE)];
  if (matches.length > 0) {
    const last = matches[matches.length - 1];
    return { body: last[1], openIdx: last.index ?? content.lastIndexOf('<conclusion') };
  }
  // No complete block. Tolerate an unclosed final <conclusion> (stream cut off
  // before </conclusion> arrived): treat from the last opening tag to end as
  // the reply rather than dumping the raw reasoning as the answer.
  let lastOpen: RegExpExecArray | null = null;
  for (let m = CONCLUSION_OPEN_RE.exec(content); m !== null; m = CONCLUSION_OPEN_RE.exec(content)) {
    lastOpen = m;
  }
  CONCLUSION_OPEN_RE.lastIndex = 0;
  if (lastOpen) {
    return { body: content.substring(lastOpen.index + lastOpen[0].length), openIdx: lastOpen.index };
  }
  return null;
}

export function parseAgentFinalContent(rawContent: string): {
  content: string;
  suggestions: string[];
  thinkingContent: string | undefined;
} {
  if (!rawContent || typeof rawContent !== 'string') {
    return { content: rawContent || '', suggestions: [], thinkingContent: undefined };
  }

  const last = findLastConclusion(rawContent);
  if (!last) {
    // No <conclusion> wrapper at all — the model omitted it. Strip
    // <suggested_actions> and show the rest (unchanged legacy fallback).
    const { content, suggestions } = parseSuggestedResponses(rawContent);
    return { content, suggestions, thinkingContent: undefined };
  }

  // Reasoning = everything before the authoritative (last) conclusion's
  // opening tag. This collapses any earlier *draft* conclusion and the
  // intervening <triage> / audit into the thinking section instead of the
  // reply. Tool markers reference positions in the full streamed text, so
  // they stay associated with thinkingContent.
  const thinkingContent = rawContent.substring(0, last.openIdx).trim() || undefined;

  // The reply body is the last block. Strip its <suggested_actions> and surface
  // them as chips — from the authoritative conclusion, not an earlier draft.
  const { content: bodyContent, suggestions } = parseSuggestedResponses(last.body);

  return { content: bodyContent.trim(), suggestions, thinkingContent };
}

export interface ToolMarkerPos {
  position: number;
  tool: string;
  message?: string;
  success?: boolean | null;
}

export interface StreamingSplit {
  workingContent: string;
  mainContent: string;
  workingMarkers: ToolMarkerPos[];
  mainMarkers: ToolMarkerPos[];
}

/**
 * Streaming-time analogue of parseAgentFinalContent. Splits the in-flight
 * raw response into "working" (pre-conclusion reasoning + tool calls) and
 * "main" (the visible answer, streamed inside <conclusion>...</conclusion>).
 *
 * Tolerates an unclosed opening <conclusion> tag (closing tag may not have
 * arrived yet) and a partial trailing <suggested_actions> block. Does not
 * trim — tool marker positions are byte offsets into the raw text, and any
 * shift would mis-align tool badges.
 *
 * When no <conclusion> opening tag has been emitted yet, the entire
 * accumulated text is treated as working content and mainContent is empty;
 * the bubble shows the collapsed "Working" pill and nothing else until the
 * tag arrives. If the stream ends without ever emitting <conclusion>, the
 * caller falls back to rendering the full text as main (see BotMessage).
 */
export function splitStreamingContent(
  rawContent: string,
  toolMarkers: ToolMarkerPos[],
): StreamingSplit {
  if (!rawContent || typeof rawContent !== 'string') {
    return { workingContent: '', mainContent: '', workingMarkers: toolMarkers || [], mainMarkers: [] };
  }

  // Strip any complete or partial trailing <suggested_actions> block. It
  // always appears at the very end of the response, after all tool markers,
  // so this never shifts marker positions.
  let cleaned = rawContent.replace(/<suggested_actions>[\s\S]*?<\/suggested_actions>/, '');
  cleaned = cleaned.replace(/<suggested_actions>[\s\S]*$/, '');

  // Use the LAST opening tag, not the first: a non-Anthropic model
  // (Gemini 3.5 Flash) can stream a draft <conclusion> in an earlier phase
  // before the real one. The most recent block is the live answer; earlier
  // ones fold into the working section. Tag-matching is attribute/case
  // tolerant — same as the finalize parser and the backend — so looser markup
  // (`<Conclusion>`, `<conclusion lang="en">`) is still detected mid-stream.
  let lastOpen: RegExpExecArray | null = null;
  for (let m = CONCLUSION_OPEN_RE.exec(cleaned); m !== null; m = CONCLUSION_OPEN_RE.exec(cleaned)) {
    lastOpen = m;
  }
  CONCLUSION_OPEN_RE.lastIndex = 0;
  if (!lastOpen) {
    return {
      workingContent: cleaned,
      mainContent: '',
      workingMarkers: toolMarkers || [],
      mainMarkers: [],
    };
  }

  const conclusionOpenIdx = lastOpen.index;
  const workingContent = cleaned.substring(0, conclusionOpenIdx);
  const afterOpen = cleaned.substring(conclusionOpenIdx + lastOpen[0].length);
  const closeMatch = afterOpen.match(/<\/conclusion\s*>/i);
  const mainContent = closeMatch ? afterOpen.substring(0, closeMatch.index) : afterOpen;

  // In normal flow all tools fire before <conclusion>. Keep any post-tag
  // markers in workingMarkers anyway so the user can still see them in the
  // thinking section instead of silently dropping them.
  return {
    workingContent,
    mainContent,
    workingMarkers: toolMarkers || [],
    mainMarkers: [],
  };
}
